import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Search, Users, Check, X, Eye, Edit, Trash2, 
  ChevronDown, DollarSign, Clock, Ban, Shield,
  UserCheck, UserX, Globe, Phone, Mail, Calendar,
  ArrowUpDown, Filter, RefreshCw, AlertTriangle
} from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import { getAllUsers, updateUser, deleteUser, adjustBalance, approveUser, suspendUser, banUser, getUserDetail } from '../services/api';
import { toast } from 'sonner';

const COUNTRY_MAP = {
  US: 'United States', GB: 'United Kingdom', CA: 'Canada', AU: 'Australia',
  DE: 'Germany', FR: 'France', JP: 'Japan', KR: 'South Korea', SG: 'Singapore',
  AE: 'UAE', IN: 'India', BR: 'Brazil', NG: 'Nigeria', ZA: 'South Africa',
  MX: 'Mexico', CH: 'Switzerland', NL: 'Netherlands', SE: 'Sweden',
  HK: 'Hong Kong', MY: 'Malaysia', PH: 'Philippines', ID: 'Indonesia',
  TH: 'Thailand', VN: 'Vietnam', PK: 'Pakistan', BD: 'Bangladesh',
  EG: 'Egypt', KE: 'Kenya', GH: 'Ghana', OTHER: 'Other'
};

const STATUS_CONFIG = {
  active: { label: 'Active', color: 'emerald', icon: UserCheck },
  pending: { label: 'Pending', color: 'amber', icon: Clock },
  unverified: { label: 'Unverified', color: 'sky', icon: Mail },
  suspended: { label: 'Suspended', color: 'orange', icon: Ban },
  banned: { label: 'Banned', color: 'red', icon: AlertTriangle },
  rejected: { label: 'Rejected', color: 'red', icon: UserX },
};

const AdminUsersPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showBalanceModal, setShowBalanceModal] = useState(false);
  const [userDetail, setUserDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', phone: '', country: '', tier: '', status: '' });
  const [balanceAmount, setBalanceAmount] = useState('');
  const [balanceOperation, setBalanceOperation] = useState('add');
  const [balanceDescription, setBalanceDescription] = useState('');
  const [stats, setStats] = useState({ total: 0, active: 0, pending: 0, unverified: 0, suspended: 0, banned: 0 });

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getAllUsers({ status: statusFilter || undefined, search: searchTerm || undefined });
      const data = response.data;
      setUsers(data);
      if (!statusFilter && !searchTerm) {
        const s = { total: data.length, active: 0, pending: 0, unverified: 0, suspended: 0, banned: 0 };
        data.forEach(u => { if (s[u.status] !== undefined) s[u.status]++; });
        setStats(s);
      }
    } catch {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, searchTerm]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleSearch = () => fetchUsers();

  const handleViewUser = async (user) => {
    setSelectedUser(user);
    setShowDetailModal(true);
    setDetailLoading(true);
    try {
      const res = await getUserDetail(user.id);
      setUserDetail(res.data);
    } catch {
      toast.error('Failed to load user details');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setEditForm({
      name: user.name || '',
      phone: user.phone || '',
      country: user.country || '',
      tier: user.tier || 'Basic',
      status: user.status || 'pending',
    });
    setShowEditModal(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      await updateUser(selectedUser.id, editForm);
      toast.success('User updated successfully');
      setShowEditModal(false);
      fetchUsers();
    } catch {
      toast.error('Failed to update user');
    }
  };

  const handleApprove = async (userId) => {
    try {
      await approveUser(userId);
      toast.success('User approved');
      fetchUsers();
    } catch {
      toast.error('Failed to approve user');
    }
  };

  const handleSuspend = async (userId) => {
    if (!window.confirm('Are you sure you want to suspend this user?')) return;
    try {
      await suspendUser(userId);
      toast.success('User suspended');
      fetchUsers();
    } catch {
      toast.error('Failed to suspend user');
    }
  };

  const handleBan = async (userId) => {
    if (!window.confirm('Are you sure you want to ban this user? This action is severe.')) return;
    try {
      await banUser(userId);
      toast.success('User banned');
      fetchUsers();
    } catch {
      toast.error('Failed to ban user');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to permanently delete this user? This cannot be undone.')) return;
    try {
      await deleteUser(userId);
      toast.success('User deleted');
      fetchUsers();
    } catch {
      toast.error('Failed to delete user');
    }
  };

  const handleBalanceAdjust = async (e) => {
    e.preventDefault();
    try {
      await adjustBalance(selectedUser.id, {
        user_id: selectedUser.id,
        amount: parseFloat(balanceAmount),
        operation: balanceOperation,
        description: balanceDescription || `Manual ${balanceOperation} by admin`
      });
      toast.success('Balance adjusted successfully');
      setShowBalanceModal(false);
      setBalanceAmount('');
      setBalanceDescription('');
      fetchUsers();
    } catch {
      toast.error('Failed to adjust balance');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatCurrency = (amount) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);

  const getCountryName = (code) => {
    if (!code) return 'Not Provided';
    return COUNTRY_MAP[code] || code;
  };

  const StatusBadge = ({ status }) => {
    const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
    const colorClasses = {
      emerald: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
      amber: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
      sky: 'bg-sky-500/15 text-sky-400 border-sky-500/30',
      orange: 'bg-orange-500/15 text-orange-400 border-orange-500/30',
      red: 'bg-red-500/15 text-red-400 border-red-500/30',
    };
    return (
      <span data-testid={`status-badge-${status}`} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${colorClasses[cfg.color]}`}>
        <cfg.icon className="w-3 h-3" />
        {cfg.label}
      </span>
    );
  };

  const kpiCards = [
    { label: 'Total Users', value: stats.total, icon: Users, color: 'from-cyan-500/20 to-cyan-500/5', iconColor: 'text-cyan-400', borderColor: 'border-cyan-500/20' },
    { label: 'Active', value: stats.active, icon: UserCheck, color: 'from-emerald-500/20 to-emerald-500/5', iconColor: 'text-emerald-400', borderColor: 'border-emerald-500/20' },
    { label: 'Unverified', value: stats.unverified, icon: Mail, color: 'from-sky-500/20 to-sky-500/5', iconColor: 'text-sky-400', borderColor: 'border-sky-500/20' },
    { label: 'Pending', value: stats.pending, icon: Clock, color: 'from-amber-500/20 to-amber-500/5', iconColor: 'text-amber-400', borderColor: 'border-amber-500/20' },
    { label: 'Suspended', value: stats.suspended, icon: Ban, color: 'from-orange-500/20 to-orange-500/5', iconColor: 'text-orange-400', borderColor: 'border-orange-500/20' },
    { label: 'Banned', value: stats.banned, icon: AlertTriangle, color: 'from-red-500/20 to-red-500/5', iconColor: 'text-red-400', borderColor: 'border-red-500/20' },
  ];

  return (
    <AdminLayout>
      <div data-testid="admin-users-page" className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-['Outfit'] text-white">User Management</h1>
          <p className="text-slate-400 mt-1">Manage all registered users and their accounts</p>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {kpiCards.map((kpi, i) => (
            <motion.div
              key={kpi.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              data-testid={`kpi-${kpi.label.toLowerCase().replace(/\s/g, '-')}`}
              className={`relative overflow-hidden rounded-xl border ${kpi.borderColor} bg-gradient-to-b ${kpi.color} p-4`}
            >
              <div className="flex items-center justify-between mb-2">
                <kpi.icon className={`w-5 h-5 ${kpi.iconColor}`} />
              </div>
              <p className="text-2xl font-bold text-white">{kpi.value}</p>
              <p className="text-xs text-slate-400 mt-1">{kpi.label}</p>
            </motion.div>
          ))}
        </div>

        {/* Search & Filters */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              className="w-full bg-slate-900/60 border border-white/10 rounded-xl pl-10 pr-4 py-3 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/30 transition-all"
              placeholder="Search by name, email, phone, or country..."
              data-testid="search-users"
            />
          </div>
          <div className="flex gap-3">
            <div className="relative">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="bg-slate-900/60 border border-white/10 rounded-xl px-4 py-3 pr-10 text-sm text-white appearance-none cursor-pointer focus:outline-none focus:border-cyan-500/50 transition-all"
                data-testid="filter-status"
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="unverified">Unverified</option>
                <option value="pending">Pending</option>
                <option value="suspended">Suspended</option>
                <option value="banned">Banned</option>
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" />
            </div>
            <button onClick={handleSearch} className="bg-cyan-500/10 text-cyan-400 border border-cyan-500/20 rounded-xl px-5 py-3 text-sm font-medium hover:bg-cyan-500/20 transition-all flex items-center gap-2" data-testid="search-btn">
              <Filter className="w-4 h-4" />
              <span className="hidden sm:inline">Filter</span>
            </button>
            <button onClick={() => { setSearchTerm(''); setStatusFilter(''); }} className="bg-slate-800/50 text-slate-400 border border-white/10 rounded-xl px-4 py-3 text-sm hover:text-white transition-all" data-testid="reset-filters">
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
        </motion.div>

        {/* Users Table - Desktop */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="hidden md:block rounded-xl border border-white/[0.06] bg-slate-900/40 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/[0.06]">
                  <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wider px-5 py-4">Full Name</th>
                  <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wider px-5 py-4">Email</th>
                  <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wider px-5 py-4">Phone</th>
                  <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wider px-5 py-4">Country</th>
                  <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wider px-5 py-4">Status</th>
                  <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wider px-5 py-4">Registered</th>
                  <th className="text-right text-xs font-semibold text-slate-400 uppercase tracking-wider px-5 py-4">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/[0.04]">
                {loading ? (
                  [...Array(5)].map((_, i) => (
                    <tr key={i}>
                      <td colSpan="7" className="px-5 py-4"><div className="h-10 bg-slate-800/50 rounded-lg animate-pulse" /></td>
                    </tr>
                  ))
                ) : users.length > 0 ? (
                  users.map((user) => (
                    <tr key={user.id} data-testid={`user-row-${user.id}`} className="hover:bg-white/[0.02] transition-colors">
                      <td className="px-5 py-4">
                        <p className="text-white font-medium text-sm truncate max-w-[160px]">{user.name || 'N/A'}</p>
                      </td>
                      <td className="px-5 py-4">
                        <p className="text-slate-300 text-sm truncate max-w-[200px]">{user.email}</p>
                      </td>
                      <td className="px-5 py-4">
                        <p className="text-slate-400 text-sm">{user.phone || 'N/A'}</p>
                      </td>
                      <td className="px-5 py-4">
                        <p className="text-slate-400 text-sm">{getCountryName(user.country)}</p>
                      </td>
                      <td className="px-5 py-4">
                        <StatusBadge status={user.status} />
                      </td>
                      <td className="px-5 py-4">
                        <p className="text-slate-500 text-sm whitespace-nowrap">{formatDate(user.created_at)}</p>
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex items-center justify-end gap-1.5">
                          <button onClick={() => handleViewUser(user)} className="p-2 rounded-lg text-slate-400 hover:text-cyan-400 hover:bg-cyan-500/10 transition-all" title="View Details" data-testid={`view-${user.id}`}>
                            <Eye className="w-4 h-4" />
                          </button>
                          <button onClick={() => handleEditUser(user)} className="p-2 rounded-lg text-slate-400 hover:text-blue-400 hover:bg-blue-500/10 transition-all" title="Edit" data-testid={`edit-${user.id}`}>
                            <Edit className="w-4 h-4" />
                          </button>
                          {(user.status === 'pending' || user.status === 'unverified') && (
                            <button onClick={() => handleApprove(user.id)} className="p-2 rounded-lg text-slate-400 hover:text-emerald-400 hover:bg-emerald-500/10 transition-all" title="Approve" data-testid={`approve-${user.id}`}>
                              <Check className="w-4 h-4" />
                            </button>
                          )}
                          {user.status === 'active' && (
                            <button onClick={() => handleSuspend(user.id)} className="p-2 rounded-lg text-slate-400 hover:text-orange-400 hover:bg-orange-500/10 transition-all" title="Suspend" data-testid={`suspend-${user.id}`}>
                              <Ban className="w-4 h-4" />
                            </button>
                          )}
                          {(user.status === 'active' || user.status === 'suspended') && (
                            <button onClick={() => handleBan(user.id)} className="p-2 rounded-lg text-slate-400 hover:text-red-400 hover:bg-red-500/10 transition-all" title="Ban" data-testid={`ban-${user.id}`}>
                              <AlertTriangle className="w-4 h-4" />
                            </button>
                          )}
                          {user.status === 'suspended' && (
                            <button onClick={() => handleApprove(user.id)} className="p-2 rounded-lg text-slate-400 hover:text-emerald-400 hover:bg-emerald-500/10 transition-all" title="Reactivate" data-testid={`reactivate-${user.id}`}>
                              <UserCheck className="w-4 h-4" />
                            </button>
                          )}
                          <button onClick={() => { setSelectedUser(user); setShowBalanceModal(true); }} className="p-2 rounded-lg text-slate-400 hover:text-cyan-400 hover:bg-cyan-500/10 transition-all" title="Adjust Balance" data-testid={`adjust-balance-${user.id}`}>
                            <DollarSign className="w-4 h-4" />
                          </button>
                          <button onClick={() => handleDeleteUser(user.id)} className="p-2 rounded-lg text-slate-400 hover:text-red-400 hover:bg-red-500/10 transition-all" title="Delete" data-testid={`delete-${user.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="text-center py-16">
                      <Users className="w-12 h-12 mx-auto mb-3 text-slate-700" />
                      <p className="text-slate-500 font-medium">No users found</p>
                      <p className="text-slate-600 text-sm mt-1">Try adjusting your search or filters</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {!loading && users.length > 0 && (
            <div className="px-5 py-3 border-t border-white/[0.06] flex items-center justify-between">
              <p className="text-xs text-slate-500">Showing {users.length} user{users.length !== 1 ? 's' : ''}</p>
            </div>
          )}
        </motion.div>

        {/* Users Cards - Mobile */}
        <div className="md:hidden space-y-3">
          {loading ? (
            [...Array(3)].map((_, i) => (
              <div key={i} className="rounded-xl border border-white/[0.06] bg-slate-900/40 p-4">
                <div className="h-20 bg-slate-800/50 rounded-lg animate-pulse" />
              </div>
            ))
          ) : users.length > 0 ? (
            users.map((user) => (
              <motion.div key={user.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                className="rounded-xl border border-white/[0.06] bg-slate-900/40 p-4 space-y-3"
              >
                <div className="flex items-start justify-between">
                  <div className="min-w-0 flex-1">
                    <p className="text-white font-semibold text-sm truncate">{user.name || 'N/A'}</p>
                    <p className="text-slate-400 text-xs truncate mt-0.5">{user.email}</p>
                  </div>
                  <StatusBadge status={user.status} />
                </div>
                <div className="grid grid-cols-3 gap-2 text-xs">
                  <div>
                    <p className="text-slate-500 mb-0.5">Phone</p>
                    <p className="text-slate-300 truncate">{user.phone || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-slate-500 mb-0.5">Country</p>
                    <p className="text-slate-300 truncate">{getCountryName(user.country)}</p>
                  </div>
                  <div>
                    <p className="text-slate-500 mb-0.5">Joined</p>
                    <p className="text-slate-300">{formatDate(user.created_at)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 pt-1 border-t border-white/[0.04]">
                  <button onClick={() => handleViewUser(user)} className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-xs font-medium text-cyan-400 bg-cyan-500/10 hover:bg-cyan-500/20 transition-all">
                    <Eye className="w-3.5 h-3.5" /> View
                  </button>
                  <button onClick={() => handleEditUser(user)} className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-xs font-medium text-blue-400 bg-blue-500/10 hover:bg-blue-500/20 transition-all">
                    <Edit className="w-3.5 h-3.5" /> Edit
                  </button>
                  {(user.status === 'pending' || user.status === 'unverified') && (
                    <button onClick={() => handleApprove(user.id)} className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-xs font-medium text-emerald-400 bg-emerald-500/10 hover:bg-emerald-500/20 transition-all">
                      <Check className="w-3.5 h-3.5" /> Approve
                    </button>
                  )}
                  {user.status === 'active' && (
                    <button onClick={() => handleSuspend(user.id)} className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-xs font-medium text-orange-400 bg-orange-500/10 hover:bg-orange-500/20 transition-all">
                      <Ban className="w-3.5 h-3.5" /> Suspend
                    </button>
                  )}
                  <button onClick={() => handleDeleteUser(user.id)} className="p-2.5 rounded-lg text-red-400 bg-red-500/10 hover:bg-red-500/20 transition-all">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </motion.div>
            ))
          ) : (
            <div className="rounded-xl border border-white/[0.06] bg-slate-900/40 p-8 text-center">
              <Users className="w-12 h-12 mx-auto mb-3 text-slate-700" />
              <p className="text-slate-500">No users found</p>
            </div>
          )}
        </div>
      </div>

      {/* User Detail Modal */}
      <AnimatePresence>
        {showDetailModal && selectedUser && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
            onClick={() => setShowDetailModal(false)}
          >
            <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
              className="w-full max-w-2xl max-h-[85vh] overflow-y-auto rounded-2xl border border-white/[0.08] bg-[#0d1117] shadow-2xl"
              onClick={(e) => e.stopPropagation()}
              data-testid="user-detail-modal"
            >
              {/* Modal Header */}
              <div className="sticky top-0 z-10 bg-[#0d1117]/95 backdrop-blur-md border-b border-white/[0.06] px-6 py-4 flex items-center justify-between">
                <h2 className="text-lg font-bold text-white">User Details</h2>
                <button onClick={() => setShowDetailModal(false)} className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-white/[0.06] transition-all" data-testid="close-detail-modal">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {detailLoading ? (
                <div className="p-8 flex items-center justify-center">
                  <div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : userDetail ? (
                <div className="p-6 space-y-6">
                  {/* User Info */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <InfoRow icon={Users} label="Full Name" value={userDetail.user.name || 'N/A'} />
                    <InfoRow icon={Mail} label="Email" value={userDetail.user.email} />
                    <InfoRow icon={Phone} label="Phone" value={userDetail.user.phone || 'N/A'} />
                    <InfoRow icon={Globe} label="Country" value={getCountryName(userDetail.user.country)} />
                    <InfoRow icon={Shield} label="Tier" value={userDetail.user.tier || 'Basic'} />
                    <InfoRow icon={Calendar} label="Registered" value={formatDate(userDetail.user.created_at)} />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-slate-400">Status:</span>
                    <StatusBadge status={userDetail.user.status} />
                  </div>

                  {/* Financial Stats */}
                  <div>
                    <h3 className="text-sm font-semibold text-slate-300 uppercase tracking-wider mb-3">Financial Overview</h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      <StatCard label="Balance" value={formatCurrency(userDetail.user.balance)} color="cyan" />
                      <StatCard label="Invested" value={formatCurrency(userDetail.user.invested_amount)} color="blue" />
                      <StatCard label="Total Profit" value={formatCurrency(userDetail.user.total_profit)} color="emerald" />
                      <StatCard label="Deposits" value={userDetail.deposits?.length || 0} color="sky" />
                      <StatCard label="Withdrawals" value={userDetail.withdrawals?.length || 0} color="orange" />
                      <StatCard label="Transactions" value={userDetail.transactions?.length || 0} color="purple" />
                    </div>
                  </div>

                  {/* Recent Transactions */}
                  {userDetail.transactions?.length > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold text-slate-300 uppercase tracking-wider mb-3">Recent Transactions</h3>
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {userDetail.transactions.slice(0, 10).map((tx) => (
                          <div key={tx.id} className="flex items-center justify-between py-2 px-3 rounded-lg bg-white/[0.02] border border-white/[0.04]">
                            <div>
                              <p className="text-xs text-white font-medium">{tx.description || tx.type}</p>
                              <p className="text-xs text-slate-500">{formatDate(tx.created_at)}</p>
                            </div>
                            <p className={`text-sm font-semibold ${tx.amount >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                              {tx.amount >= 0 ? '+' : ''}{formatCurrency(tx.amount)}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex flex-wrap gap-2 pt-2 border-t border-white/[0.06]">
                    <button onClick={() => { setShowDetailModal(false); handleEditUser(selectedUser); }} className="px-4 py-2 rounded-lg text-sm font-medium text-blue-400 bg-blue-500/10 border border-blue-500/20 hover:bg-blue-500/20 transition-all">
                      Edit User
                    </button>
                    <button onClick={() => { setShowDetailModal(false); setShowBalanceModal(true); }} className="px-4 py-2 rounded-lg text-sm font-medium text-cyan-400 bg-cyan-500/10 border border-cyan-500/20 hover:bg-cyan-500/20 transition-all">
                      Adjust Balance
                    </button>
                    {(selectedUser.status === 'pending' || selectedUser.status === 'unverified') && (
                      <button onClick={() => { handleApprove(selectedUser.id); setShowDetailModal(false); }} className="px-4 py-2 rounded-lg text-sm font-medium text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 hover:bg-emerald-500/20 transition-all">
                        Approve
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                <div className="p-8 text-center text-slate-500">Failed to load user details</div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Edit User Modal */}
      <AnimatePresence>
        {showEditModal && selectedUser && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
            onClick={() => setShowEditModal(false)}
          >
            <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
              className="w-full max-w-md rounded-2xl border border-white/[0.08] bg-[#0d1117] shadow-2xl"
              onClick={(e) => e.stopPropagation()}
              data-testid="edit-user-modal"
            >
              <div className="border-b border-white/[0.06] px-6 py-4 flex items-center justify-between">
                <h2 className="text-lg font-bold text-white">Edit User</h2>
                <button onClick={() => setShowEditModal(false)} className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-white/[0.06] transition-all" data-testid="close-edit-modal">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <form onSubmit={handleEditSubmit} className="p-6 space-y-4">
                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-1.5">Full Name</label>
                  <input type="text" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                    className="w-full bg-slate-900/60 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-cyan-500/50 transition-all"
                    data-testid="edit-name"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-1.5">Phone</label>
                  <input type="text" value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                    className="w-full bg-slate-900/60 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-cyan-500/50 transition-all"
                    data-testid="edit-phone"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-1.5">Country</label>
                  <select value={editForm.country} onChange={(e) => setEditForm({ ...editForm, country: e.target.value })}
                    className="w-full bg-slate-900/60 border border-white/10 rounded-xl px-4 py-3 text-sm text-white appearance-none focus:outline-none focus:border-cyan-500/50 transition-all"
                    data-testid="edit-country"
                  >
                    <option value="">Select country</option>
                    {Object.entries(COUNTRY_MAP).map(([code, name]) => (
                      <option key={code} value={code}>{name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-1.5">Tier</label>
                  <select value={editForm.tier} onChange={(e) => setEditForm({ ...editForm, tier: e.target.value })}
                    className="w-full bg-slate-900/60 border border-white/10 rounded-xl px-4 py-3 text-sm text-white appearance-none focus:outline-none focus:border-cyan-500/50 transition-all"
                    data-testid="edit-tier"
                  >
                    <option value="Basic">Basic</option>
                    <option value="Premium">Premium</option>
                    <option value="VIP">VIP</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-1.5">Status</label>
                  <select value={editForm.status} onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                    className="w-full bg-slate-900/60 border border-white/10 rounded-xl px-4 py-3 text-sm text-white appearance-none focus:outline-none focus:border-cyan-500/50 transition-all"
                    data-testid="edit-status"
                  >
                    <option value="active">Active</option>
                    <option value="pending">Pending</option>
                    <option value="unverified">Unverified</option>
                    <option value="suspended">Suspended</option>
                    <option value="banned">Banned</option>
                  </select>
                </div>
                <div className="flex gap-3 pt-2">
                  <button type="button" onClick={() => setShowEditModal(false)} className="flex-1 py-3 rounded-xl text-sm font-medium text-slate-400 bg-slate-800/50 border border-white/10 hover:text-white transition-all">
                    Cancel
                  </button>
                  <button type="submit" className="flex-1 py-3 rounded-xl text-sm font-medium text-white bg-gradient-to-r from-cyan-500 to-emerald-500 hover:opacity-90 transition-all" data-testid="save-edit">
                    Save Changes
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Balance Adjustment Modal */}
      <AnimatePresence>
        {showBalanceModal && selectedUser && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
            onClick={() => setShowBalanceModal(false)}
          >
            <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
              className="w-full max-w-md rounded-2xl border border-white/[0.08] bg-[#0d1117] shadow-2xl"
              onClick={(e) => e.stopPropagation()}
              data-testid="balance-modal"
            >
              <div className="border-b border-white/[0.06] px-6 py-4 flex items-center justify-between">
                <h2 className="text-lg font-bold text-white">Adjust Balance</h2>
                <button onClick={() => setShowBalanceModal(false)} className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-white/[0.06] transition-all">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-6">
                <p className="text-sm text-slate-400 mb-1">User: <span className="text-white font-medium">{selectedUser.name}</span></p>
                <p className="text-sm text-slate-400 mb-5">Current Balance: <span className="text-cyan-400 font-semibold">{formatCurrency(selectedUser.balance)}</span></p>
                <form onSubmit={handleBalanceAdjust} className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <button type="button" onClick={() => setBalanceOperation('add')}
                      className={`p-3 rounded-xl border text-sm font-medium transition-all ${balanceOperation === 'add' ? 'border-emerald-500/50 bg-emerald-500/10 text-emerald-400' : 'border-white/10 text-slate-500 hover:text-white'}`}
                    >
                      Add Funds
                    </button>
                    <button type="button" onClick={() => setBalanceOperation('subtract')}
                      className={`p-3 rounded-xl border text-sm font-medium transition-all ${balanceOperation === 'subtract' ? 'border-red-500/50 bg-red-500/10 text-red-400' : 'border-white/10 text-slate-500 hover:text-white'}`}
                    >
                      Subtract
                    </button>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-400 mb-1.5">Amount</label>
                    <input type="number" value={balanceAmount} onChange={(e) => setBalanceAmount(e.target.value)}
                      className="w-full bg-slate-900/60 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-cyan-500/50 transition-all"
                      placeholder="0.00" min="0.01" step="0.01" required data-testid="balance-amount"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-400 mb-1.5">Description</label>
                    <input type="text" value={balanceDescription} onChange={(e) => setBalanceDescription(e.target.value)}
                      className="w-full bg-slate-900/60 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-cyan-500/50 transition-all"
                      placeholder="Reason for adjustment"
                    />
                  </div>
                  <div className="flex gap-3 pt-2">
                    <button type="button" onClick={() => setShowBalanceModal(false)} className="flex-1 py-3 rounded-xl text-sm font-medium text-slate-400 bg-slate-800/50 border border-white/10 hover:text-white transition-all">
                      Cancel
                    </button>
                    <button type="submit" className="flex-1 py-3 rounded-xl text-sm font-medium text-white bg-gradient-to-r from-cyan-500 to-emerald-500 hover:opacity-90 transition-all" data-testid="confirm-balance">
                      Confirm
                    </button>
                  </div>
                </form>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </AdminLayout>
  );
};

const InfoRow = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-3 py-2 px-3 rounded-lg bg-white/[0.02] border border-white/[0.04]">
    <Icon className="w-4 h-4 text-slate-500 flex-shrink-0" />
    <div className="min-w-0">
      <p className="text-xs text-slate-500">{label}</p>
      <p className="text-sm text-white truncate">{value}</p>
    </div>
  </div>
);

const StatCard = ({ label, value, color }) => {
  const colorMap = {
    cyan: 'border-cyan-500/20 bg-cyan-500/5 text-cyan-400',
    blue: 'border-blue-500/20 bg-blue-500/5 text-blue-400',
    emerald: 'border-emerald-500/20 bg-emerald-500/5 text-emerald-400',
    sky: 'border-sky-500/20 bg-sky-500/5 text-sky-400',
    orange: 'border-orange-500/20 bg-orange-500/5 text-orange-400',
    purple: 'border-purple-500/20 bg-purple-500/5 text-purple-400',
  };
  return (
    <div className={`rounded-xl border p-3 ${colorMap[color]}`}>
      <p className="text-xs text-slate-500">{label}</p>
      <p className={`text-lg font-bold mt-0.5 ${colorMap[color].split(' ').pop()}`}>{value}</p>
    </div>
  );
};

export default AdminUsersPage;
